import moment from "moment";
import { Cell } from "react-table";
import { ConnectedInfo, QuestionAnswerModel, QuestionListModel } from "src/api/question/question-type";
import { BaseTooltip } from "src/components";
import DetailLink from "src/components/DetailLink";
import { PagePath } from "src/pages/product/details";
import { YmdFormat, numberToStringWithComma } from "src/utils";
import { siteOptions, statusOptions } from "../../question-types";
import { convertMinutesToDHMS } from "src/pages/product/forms/commonSpaceForm/libs";
import BaseNewTabLink from "src/components/BaseNewTabLink";

const QuestionStatusMap = {
  STATUS_UNRECOGNIZED: "정의되지 않은 상태",
  QUESTION_WAIT: "접수",
  QUESTION_PROCEEDING: "처리중",
  QUESTION_HOLD: "보류",
  QUESTION_COMPLETE: "완료",
  QUESTION_CANCELED: "취소",
} as const;

const SiteMap = {
  SITE_HOMEPAGE: "홈페이지",
  SITE_EXTERNAL: "외부 플랫폼",
  SITE_ETC: "기타",
  SITE_TAAP_SPACE: "TaapSpace",
  SITE_TAAP: "Taap",
} as const;

const QuestionColmns = [
  {
    Header: "VoC ID",
    accessor: "id",
    width: 120,
    sticky: "left",
    Cell: ({ row, value }: Cell<QuestionListModel>) => {
      const detailPath = `${PagePath.question.detail.replace(":id", value)}`;
      return (
        <DetailLink to={detailPath} className="text-underline">
          {value}
        </DetailLink>
      );
    },
    excelValue: (row: QuestionListModel) => {
      return row.id ?? "-";
    },
  },
  {
    Header: "상태",
    accessor: "status",
    width: 80,
    sticky: "left",
    Cell: ({ value }: Cell<QuestionListModel>) => {
      let status = statusOptions.find((option) => option.value === value)?.label;
      let color = statusOptions.find((option) => option.value === value)?.tagColor;

      return <div className="w-100 flex-center-center"><span className={color}>{status}</span></div>;
    },
    excelValue: (row: QuestionListModel) => {
      return QuestionStatusMap[row.status as keyof typeof QuestionStatusMap] ?? "-";
    },
  },
  {
    Header: "구분",
    accessor: "csCategoryType",
    width: 80,
    sticky: "left",
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <p className="w-100 text-center">{value || "-"}</p>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.csCategoryType ?? "-";
    },
  },

  {
    Header: "유형",
    accessor: "csTypeName",
    width: 90,
    sticky: "left",
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <p className="w-100 text-center">{value || "-"}</p>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.csTypeName ?? "-";
    },
  },
  {
    Header: "항목",
    accessor: "summary",
    width: 140,
    sticky: "left",
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <BaseTooltip className="text-left w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.summary ?? "-";
    },
  },
  {
    Header: "유입경로",
    accessor: "site",
    width: 120,
    sticky: "left",
    Cell: ({ value }: Cell<QuestionListModel>) => {
      const site = siteOptions.find((data) => data.value === value)?.label;
      return <span className="text-center w-100">{site || "-"}</span>;
    },
    excelValue: (row: QuestionListModel) => {
      return SiteMap[row.site as keyof typeof SiteMap] ?? "-";
    },
  },
  {
    Header: "접수 내용",
    accessor: "description",
    width: 160,
    sticky: "left",
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <BaseTooltip className="text-left w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.description ?? "-";
    },
  },

  {
    Header: "접수일시",
    accessor: "createdDate",
    width: 160,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <p className="w-100 text-center">{value && moment(value).format(YmdFormat.FULL)}</p>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.createdDate ? moment(row.createdDate).format(YmdFormat.FULL) : "-";
    },
  },

  {
    Header: "건물명",
    accessor: "buildingName",
    width: 120,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <BaseTooltip isSingleLine={false} className="text-left w-full" contents={value ?? "-"} />;
    },
    excelValue: (row: QuestionListModel) => {
      return row.buildingName ?? "-";
    },
  },

  {
    Header: "세부 위치",
    accessor: "floorNum",
    width: 80,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return (
        <BaseTooltip className="text-left w-100" contents={value ? (value > 0 ? `지상${value}층` : `지하${Math.abs(value)}층`) : "-"}></BaseTooltip>
      );
    },
    excelValue: (row: QuestionListModel) => {
      return row.floorNum ? (row.floorNum > 0 ? `지상${row.floorNum}층` : `지하${Math.abs(row.floorNum)}층`) : "-";
    },
  },
  {
    Header: "접수자명",
    accessor: "reporterName",
    width: 120,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      //CS 문의 정보 > 이름/닉네임 출력
      return <BaseTooltip className="text-left w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.reporterName ?? "-";
    },
  },
  {
    Header: "접수자 소속 회사",
    accessor: "mbOrganizationName",
    width: 160,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      //*소속 회사란? 신청/계약의 법인명/상호 정보

      return <BaseTooltip className="text-left w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.mbOrganizationName ?? "-";
    },
  },
  {
    Header: "접수자 신청/계약",
    accessor: "contractName",
    width: 160,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      //연결된 신청/계약이 있는 경우 계약명 출력, 없으면 하이픈 출력
      //계약명 정보 없으면 '상품명' 출력
      return <BaseTooltip className="text-left w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.contractName ?? "-";
    },
  },
  {
    Header: "답변 내용",
    accessor: "answerList",
    width: 160,
    Cell: ({ row }: Cell<QuestionListModel>) => {
      const answer = row?.original?.answerList?.sort((a: QuestionAnswerModel, b: QuestionAnswerModel) => Number(b.id) - Number(a.id))?.[0]
        ?.description;

      return <BaseTooltip className="text-left w-100" contents={answer ?? "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      const answer = row.answerList?.sort((a: QuestionAnswerModel, b: QuestionAnswerModel) => Number(b.id) - Number(a.id))?.[0]?.description;
      return answer ?? "-";
    },
  },


  {
    Header: "최종 처리자",
    accessor: "completedBy", // 완료처리자
    width: 120,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      //최종 처리자 : 상태값을 '완료'로 변경한 파트너 회원 → 이메일 출력
      return <BaseTooltip className="text-left w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.completedBy ?? "-";
    },
  },
  {
    Header: "처리자", //기존 담당자
    accessor: "assigneeEmail",
    width: 120,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <BaseTooltip className="text-left w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.assigneeEmail ?? "-";
    },
  },

  {
    Header: "최종 처리일시",
    accessor: "completedDate", // 완료처리일시
    width: 160,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      if (value === null) {
        return <span>-</span>;
      }

      return <p className="w-100 text-center">{value && moment(value).format(YmdFormat.FULL)}</p>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.completedDate ? moment(row.completedDate).format(YmdFormat.FULL) : "-";
    },
  },
  {
    Header: "처리 소요시간",
    accessor: "durationTime",
    width: 120,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <BaseTooltip className="text-left w-100" contents={value === 0 ? "0분" : convertMinutesToDHMS(value) ?? "-"}></BaseTooltip>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.durationTime ?? "-";
    },
  },
  {
    Header: "매출액(원)",
    accessor: "cost",
    width: 120,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <BaseTooltip className="text-right w-100" contents={value !== 0 ? numberToStringWithComma(value) : "-"} isSingleLine />
    },
    excelValue: (row: QuestionListModel) => {
      return row.cost ? numberToStringWithComma(row.cost) : "-";
    },
  },
  {
    Header: "지출액(원)",
    accessor: "expense",
    width: 120,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <BaseTooltip className="text-right w-100" contents={value !== 0 ? numberToStringWithComma(value) : "-"} isSingleLine />
    },
    excelValue: (row: QuestionListModel) => {
      return row.expense ? numberToStringWithComma(row.expense) : "-";
    },
  },
  {
    Header: () => {
      return <span className="no-wrap">연결된 워크오더</span>;
    },
    accessor: "connectedInfoList",
    width: 120,
    Cell: ({ row, value }: Cell<QuestionListModel>) => {
      const count = value.filter((item: ConnectedInfo) => {
        return item.connectedType === "CS_CONNECTED_WORKORDER";
      })?.length;
      const detailPath = `${PagePath.question.detail.replace(":id", `${row.original.id}`)}#연결%20정보`;
      // return <span className="w-100"></span>;
      return <BaseNewTabLink path={detailPath} value={count === 0 ? "미연결" : `연결됨(${count})`}></BaseNewTabLink>;
    },
    excelValue: (row: QuestionListModel) => {
      const count = row.connectedInfoList?.filter((item: ConnectedInfo) => {
        return item.connectedType === "CS_CONNECTED_WORKORDER";
      })?.length;
      return count === 0 ? "미연결" : `연결됨(${count})`;
    },
  },
  {
    Header: "최종 수정일시",
    accessor: "modifiedDate",
    width: 160,
    Cell: ({ value }: Cell<QuestionListModel>) => {
      return <p className="w-100 text-center">{value && moment(value).format(YmdFormat.FULL)}</p>;
    },
    excelValue: (row: QuestionListModel) => {
      return row.modifiedDate ? moment(row.modifiedDate).format(YmdFormat.FULL) : "-";
    },
  },
];

export default QuestionColmns;
